import { useEffect } from 'react';
import { Card, CardContent, CardHeader, Grid, Typography } from '@mui/material';
import { theme } from 'theme';
import ContextIcon from 'assets/icons/Context.svg?react';
import CustomCircularProgress from 'components/common/CustomCircularProgress';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsVILoading, selectVIDetails } from 'selectors/videoIdentificationSelector';
import { fetchVideoIdentificationDetails } from 'reducers/videoIdentificationReducer';
import { DividerTitle } from 'components/common/DividerTitle';
import { formatEnum } from 'utils/stringUtils';
import { VideoIdentificationDetailsActions } from 'views/VideoIdentification/VideoIdentificationDetailsActions';
import moment from 'moment';
import { getName } from 'country-list';
import { LanguagesEnum } from 'types';
import { AppDispatch } from 'store';

const styles = {
  container: {
    paddingLeft: theme.spacing(2),
  },
  outer: {
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
  },
  icon: {
    marginTop: theme.spacing(2),
  },
  cardHeader: {
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  card: {
    maxWidth: '90%',
    marginBottom: theme.spacing(12),
  },
  spinner: {
    marginTop: theme.spacing(1),
  },
  title: {
    paddingBottom: theme.spacing(2),
  },
};

type Props = {
  productId?: string;
};

export const VideoIdentificationDetails = ({ productId }: Props) => {
  const isLoading = useSelector(selectIsVILoading);
  const item = useSelector(selectVIDetails);
  const dispatch = useDispatch<AppDispatch>();

  const {
    businessRelationshipNumber = 'N/A',
    name = {
      firstName: 'N/A',
      middleName: 'N/A',
      lastName: 'N/A',
      gender: 'N/A',
    },
    nationality = 'N/A',
    dateOfBirth,
    address = {
      country: 'N/A',
      city: 'N/A',
      street: 'N/A',
      zip: 'N/A',
    },
    emailAddress = 'N/A',
    mobileNumber = 'N/A',
    language,
    clmUserInfo = {
      uNumber: 'N/A',
      fullName: 'N/A',
      emailAddress: 'N/A',
    },
  } = item ?? {};

  const formattedDate = dateOfBirth ? moment(dateOfBirth).format('DD.MM.YYYY') : 'N/A';
  const formattedCountry = getName(address.country ?? '') ?? 'N/A';
  const formattedNationality = getName(nationality) ?? 'N/A';

  useEffect(() => {
    if (productId) {
      dispatch(fetchVideoIdentificationDetails(productId));
    }
  }, [productId, dispatch]);

  return (
    <>
      <Grid data-testid="video-identification-details" sx={styles.outer}>
        <Typography sx={styles.title} variant="h1">
          VIDEO IDENTIFICATION
        </Typography>
        <Card sx={styles.card} square>
          <Grid container sx={styles.container}>
            <ContextIcon style={styles.icon} />
            <CardHeader
              title={'DEFINED CONTEXT'}
              titleTypographyProps={{ variant: 'subtitle1' }}
              sx={styles.cardHeader}
            />
          </Grid>
          <CardContent>
            {isLoading ? (
              <Grid
                id={'video-identification-defined-context-circular-progress'}
                data-testid={'video-identification-defined-context-circular-progress'}
                sx={styles.spinner}
              >
                <CustomCircularProgress size={30} />
              </Grid>
            ) : (
              <Grid container direction={'row'} spacing={2}>
                <Grid item xs={12}>
                  <DividerTitle text={'Business relationship'} />
                </Grid>
                <Grid item container direction={'column'} xs={12}>
                  <Grid item>
                    <Typography variant={'caption'}>BR</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{businessRelationshipNumber}</Typography>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <DividerTitle text={'Partner data'} />
                </Grid>
                <Grid item container direction={'column'} xs={4}>
                  <Grid item>
                    <Typography variant={'caption'}>First name</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{name.firstName}</Typography>
                  </Grid>
                </Grid>
                <Grid item container direction={'column'} xs={4}>
                  <Grid item>
                    <Typography variant={'caption'}>Middle name</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{name.middleName}</Typography>
                  </Grid>
                </Grid>
                <Grid item container direction={'column'} xs={4}>
                  <Grid item>
                    <Typography variant={'caption'}>Last name</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{name.lastName}</Typography>
                  </Grid>
                </Grid>
                <Grid item container direction={'column'} xs={4}>
                  <Grid item>
                    <Typography variant={'caption'}>Nationality</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{formattedNationality}</Typography>
                  </Grid>
                </Grid>
                <Grid item container direction={'column'} xs={4}>
                  <Grid item>
                    <Typography variant={'caption'}>Date of birth</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{formattedDate}</Typography>
                  </Grid>
                </Grid>
                <Grid item container direction={'column'} xs={4}>
                  <Grid item>
                    <Typography variant={'caption'}>Gender</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{name.gender ? formatEnum(name.gender) : 'N/A'}</Typography>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <DividerTitle text={'Main domicile'} />
                </Grid>
                <Grid item container direction={'column'} xs={4}>
                  <Grid item>
                    <Typography variant={'caption'}>Country</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{formattedCountry}</Typography>
                  </Grid>
                </Grid>
                <Grid item container direction={'column'} xs={8}>
                  <Grid item>
                    <Typography variant={'caption'}>City</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{address.city}</Typography>
                  </Grid>
                </Grid>
                <Grid item container direction={'column'} xs={4}>
                  <Grid item>
                    <Typography variant={'caption'}>Street</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{address.street}</Typography>
                  </Grid>
                </Grid>
                <Grid item container direction={'column'} xs={8}>
                  <Grid item>
                    <Typography variant={'caption'}>Zip Code</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{address.zip}</Typography>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <DividerTitle text={'Contact'} />
                </Grid>
                <Grid item container direction={'column'} xs={4}>
                  <Grid item>
                    <Typography variant={'caption'}>E-Mail</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{emailAddress}</Typography>
                  </Grid>
                </Grid>
                <Grid item container direction={'column'} xs={4}>
                  <Grid item>
                    <Typography variant={'caption'}>Mobile number</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{mobileNumber}</Typography>
                  </Grid>
                </Grid>
                <Grid item container direction={'column'} xs={4}>
                  <Grid item>
                    <Typography variant={'caption'}>Preferred language</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{language ? LanguagesEnum[language as keyof typeof LanguagesEnum] : 'N/A'}</Typography>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <DividerTitle text={'Relationship manager'} />
                </Grid>
                <Grid item container direction={'column'} xs={4}>
                  <Grid item>
                    <Typography variant={'caption'}>Name</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{clmUserInfo.fullName}</Typography>
                  </Grid>
                </Grid>
                <Grid item container direction={'column'} xs={4}>
                  <Grid item>
                    <Typography variant={'caption'}>E-Mail</Typography>
                  </Grid>
                  <Grid item>
                    <Typography>{clmUserInfo.emailAddress}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>
      <VideoIdentificationDetailsActions />
    </>
  );
};
